import { CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons'
import { Tag } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActionEnum } from '../../../common'
import {
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { ApiMethodEnum, GroupApiEntity, UserTypeEnum } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadApis,
  loadUserTypes,
  setAction,
  setSelectedApi,
} from '../../../stores/actions'
import { ManageApiModalComponent } from './components'

export const APIPage: React.FC = () => {
  const dispatch = useDispatch()
  const { apis, isApisLoaded } = useSelector((state: RootState) => state.api)
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { userTypes, isUserTypesLoaded } = useSelector(
    (state: RootState) => state.user
  )

  const groupApis = React.useMemo(() => {
    const map = apis.reduce<{ [key: string]: GroupApiEntity }>(
      (acc, curr) => ({
        ...acc,
        [curr.path]: acc[curr.path]
          ? {
              key: curr.path,
              path: curr.path,
              accessibleUserTypes: [],
              children: [
                ...acc[curr.path].children,
                {
                  ...curr,
                  key: curr.id,
                },
              ],
            }
          : {
              key: curr.path,
              path: curr.path,
              accessibleUserTypes: [],
              children: [
                {
                  ...curr,
                  key: curr.id,
                },
              ],
            },
      }),
      {} as { [key: string]: GroupApiEntity }
    )
    return Object.keys(map).map((key) => map[key])
  }, [apis])

  React.useEffect(() => {
    dispatch(loadApis())
    dispatch(loadUserTypes())
    // eslint-disable-next-line
  }, [])

  const columns = React.useMemo(
    () => [
      {
        title: translate('api.name', language),
        render: (_: any, api: any) =>
          api.children
            ? `${translate('common.manage', language)} ${translate(
                `api.name.${api.path.replace(/\//, '')}`,
                language
              )}`
            : `${translate(
                `api.method.${api.method.toLowerCase()}.detail`,
                language
              )} ${translate(
                `api.name.${api.path.replace(/\//, '')}`,
                language
              )}`,
        width: 250,
      },
      {
        title: translate('api.path', language),
        dataIndex: 'path',
        render: (value: any, api: any) =>
          api.children ? <Tag>{value}</Tag> : '',
        width: 130,
      },
      {
        dataIndex: 'method',
        title: translate('api.method', language),
        render: (method: any) =>
          method && (
            <Tag
              style={{ fontSize: '0.7rem' }}
              color={
                method === ApiMethodEnum.GET
                  ? 'processing'
                  : method === ApiMethodEnum.POST
                  ? 'success'
                  : method === ApiMethodEnum.PUT
                  ? 'warning'
                  : 'error'
              }
              key={method}
            >
              {translate(`api.method.${method.toLowerCase()}`, language)}
            </Tag>
          ),
        width: 130,
      },
      ...userTypes
        .filter((item) => item.type !== UserTypeEnum.ADMIN)
        .map((userType) => {
          return {
            dataIndex: 'accessibleUserTypes',
            title: (
              <Tag
                color={userType.type === UserTypeEnum.CUSTOMER ? 'blue' : 'red'}
                key={userType.id}
              >
                {translate(
                  `user.userType.${userType.type.toLowerCase()}`,
                  language
                )}
              </Tag>
            ),
            render: (accessibleUserTypes: any, api: any) => {
              const activated = accessibleUserTypes
                .map((item: any) => item.id)
                .includes(userType.id)
              return Object.keys(api).includes('method') ? (
                <Tag
                  icon={
                    activated ? (
                      <CheckCircleOutlined />
                    ) : (
                      <MinusCircleOutlined />
                    )
                  }
                  color={activated ? 'success' : 'default'}
                >
                  {translate(
                    `api.status.${activated ? 'open' : 'block'}`,
                    language
                  )}
                </Tag>
              ) : (
                <></>
              )
            },
            width: 130,
          }
        }),
      {
        title: translate('common.action', language),
        render: (_: any, api: any) =>
          Object.keys(api).includes('method') && (
            <TableActionColumnButtonComponent
              onViewButtonClick={() => {
                dispatch(setSelectedApi(api))
                dispatch(setAction(ActionEnum.VIEW))
              }}
            />
          ),
        width: 130,
      },
    ],
    [dispatch, language, userTypes]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showManageModal && <ManageApiModalComponent />}
      <PageTitleComponent />
      <TableComponent
        paginationConfig={{ pageSize: 50 }}
        data={groupApis}
        columns={columns}
        loading={!isApisLoaded || !isUserTypesLoaded}
      />
    </div>
  )
}
